import type { AssistantConversationId, SuretyAccountId } from "@inrev/common";
import { Option } from "@inrev/core";
import { useFetchAssistantConversations } from "@portal/api/assistant/admin";
import { ReportedRetryableLoadError } from "@portal/components/features/load-errors";
import { dockedPanelScrollRegionProps } from "@portal/components/layout/DockedPanel";
import { cn } from "@portal/components/lib/utils";
import { EmptyState } from "@portal/components/patterns/empty-state";
import { Button, List, Spinner, Text } from "@portal/components/primitives";
import { DateTime } from "luxon";
import { useEffect, useRef } from "react";
import {
	assistantAskQuestionLabel,
	assistantHistoryEmptyCopy,
	assistantHistoryEmptyTitle,
	assistantHistoryHeading,
} from "../../admin/shared/tools/assistant/assistantCopy";
import type { AssistantConversationSelection } from "../../admin/shared/tools/assistant/useStoredAssistantConversation";
import { groupConversationsByActivity } from "./groupConversationsByActivity";
import { VerniHistoryRow, type VerniHistoryVariant } from "./VerniHistoryRow";

const LOAD_MORE_ROOT_MARGIN = "0px 0px 120px 0px";

type VerniHistoryListProps = {
	accountId: SuretyAccountId;
	accountName: string;
	/** `compact` sits in the overlay under a heading; `rail` is the expanded view's left column. */
	variant: VerniHistoryVariant;
	/** Moves focus into the list once it loads: the first row, or Ask a question when empty. */
	autoFocus?: boolean;
	selectedConversationId?: AssistantConversationSelection;
	onSelect: (conversationId: AssistantConversationId) => void;
	onNewConversation: () => void;
};

/** The current admin's Verni conversations on an account, grouped by when they were last active. */
export const VerniHistoryList = ({
	accountId,
	accountName,
	variant,
	autoFocus = false,
	selectedConversationId,
	onSelect,
	onNewConversation,
}: VerniHistoryListProps) => {
	const {
		conversations,
		conversationsError,
		conversationsIsFetching,
		refetchConversations,
		hasMoreConversations,
		loadingMoreConversations,
		loadMoreConversationsFailed,
		loadMoreConversations,
	} = useFetchAssistantConversations(accountId);
	const scrollRef = useRef<HTMLDivElement>(null);
	const sentinelRef = useRef<HTMLDivElement>(null);
	const firstRowRef = useRef<HTMLButtonElement>(null);
	const askRef = useRef<HTMLButtonElement>(null);
	const canLoadMore =
		hasMoreConversations && !loadingMoreConversations && !loadMoreConversationsFailed;

	useEffect(() => {
		const sentinel = sentinelRef.current;
		if (!canLoadMore || sentinel === null) return;
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry?.isIntersecting) void loadMoreConversations();
			},
			{ root: scrollRef.current, rootMargin: LOAD_MORE_ROOT_MARGIN },
		);
		observer.observe(sentinel);
		return () => observer.disconnect();
	}, [canLoadMore, loadMoreConversations]);

	const isEmpty = conversations?.length === 0;
	const loaded = Option.isSome(conversations);

	useEffect(() => {
		if (!autoFocus || !loaded) return;
		(isEmpty ? askRef : firstRowRef).current?.focus();
	}, [autoFocus, loaded, isEmpty]);

	const body = () => {
		if (Option.isNone(conversations) && Option.isSome(conversationsError)) {
			return (
				<ReportedRetryableLoadError
					className="mx-2 mt-3"
					error={conversationsError}
					onRetry={() => void refetchConversations()}
					resource="your conversations"
					retrying={conversationsIsFetching}
					testId="verni-history-error"
				/>
			);
		}
		if (Option.isNone(conversations)) {
			return (
				<div
					className="flex flex-1 items-center justify-center"
					data-testid="verni-history-loading"
				>
					<Spinner />
				</div>
			);
		}
		if (isEmpty) {
			return (
				<EmptyState
					action={
						<Button
							color="primary"
							data-testid="verni-history-ask"
							onClick={onNewConversation}
							ref={askRef}
							size="sm"
							trackingId="verni-history-ask-question"
							variant="outline"
						>
							{assistantAskQuestionLabel}
						</Button>
					}
					className="px-9 py-6"
					icon="clock"
					message={assistantHistoryEmptyCopy(accountName)}
					testId="verni-history-empty"
					title={assistantHistoryEmptyTitle}
				/>
			);
		}

		const now = DateTime.now();
		return (
			<>
				{groupConversationsByActivity(conversations, now).map((group, groupIndex) => (
					<div data-testid="verni-history-group" key={group.label}>
						<Text
							className={cn(
								"block px-2 pb-1 tracking-[0.06em]",
								variant === "rail" ? "pt-3" : "pt-2.5",
							)}
							color="tertiary"
							type="eyebrow"
						>
							{group.label}
						</Text>
						<List className="space-y-0.5">
							{group.items.map((conversation, index) => (
								<VerniHistoryRow
									conversation={conversation}
									key={conversation.id}
									now={now}
									onSelect={onSelect}
									ref={groupIndex === 0 && index === 0 ? firstRowRef : undefined}
									selected={conversation.id === selectedConversationId}
									variant={variant}
								/>
							))}
						</List>
					</div>
				))}
				<div aria-hidden="true" data-testid="verni-history-sentinel" ref={sentinelRef} />
				{loadingMoreConversations && (
					<div className="flex justify-center py-2" data-testid="verni-history-loading-more">
						<Spinner />
					</div>
				)}
				{loadMoreConversationsFailed && Option.isSome(conversationsError) && (
					<ReportedRetryableLoadError
						className="mt-2"
						error={conversationsError}
						onRetry={() => void loadMoreConversations()}
						resource="older conversations"
						retrying={loadingMoreConversations}
						testId="verni-history-load-more-error"
					/>
				)}
			</>
		);
	};

	return (
		<div className="flex min-h-0 flex-1 flex-col" data-testid="verni-history">
			{variant === "compact" && !isEmpty && (
				<div className="px-3.5 pb-2 pt-3">
					<Text className="text-[13px]" color="primary" type="body-strong">
						{assistantHistoryHeading}
					</Text>
				</div>
			)}
			<div
				className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-y-contain px-2 pb-3"
				ref={scrollRef}
				{...dockedPanelScrollRegionProps}
			>
				{body()}
			</div>
		</div>
	);
};
